import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "uploadArea", "uploadPrompt", "uploadProgress", "progressBar", "imagePreview", "previewImage", "uploadError", "removeButton", "currentImage"]
  static values = { albumId: Number, uploadUrl: String }

  connect() {
    this.setupEventListeners()
    // Get album ID from the form if not provided
    if (!this.hasAlbumIdValue) {
      const form = this.element.closest('form')
      if (form) {
        const match = form.action.match(/albums\/(\d+)/)
        if (match) {
          this.albumIdValue = parseInt(match[1])
        }
      }
    }
  }

  setupEventListeners() {
    // File input change
    this.inputTarget.addEventListener('change', (e) => this.handleFileSelect(e))
    
    // Drag and drop
    this.uploadAreaTarget.addEventListener('dragover', (e) => this.handleDragOver(e))
    this.uploadAreaTarget.addEventListener('dragleave', (e) => this.handleDragLeave(e))
    this.uploadAreaTarget.addEventListener('drop', (e) => this.handleDrop(e))
    
    // Remove image
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.addEventListener('click', () => this.removeImage())
    }
  }

  handleDragOver(e) {
    e.preventDefault()
    this.uploadAreaTarget.classList.add('border-indigo-400', 'bg-indigo-50')
  }

  handleDragLeave(e) {
    e.preventDefault()
    this.uploadAreaTarget.classList.remove('border-indigo-400', 'bg-indigo-50')
  }

  handleDrop(e) {
    e.preventDefault()
    this.uploadAreaTarget.classList.remove('border-indigo-400', 'bg-indigo-50')
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.processFile(files[0])
    }
  }

  handleFileSelect(e) {
    if (e.target.files.length > 0) {
      this.processFile(e.target.files[0])
    }
  }

  processFile(file) {
    // Clear any previous errors
    this.hideError()
    
    // Validate file type
    if (!file.type.startsWith('image/jpeg')) {
      this.showError('Please select a JPG image file.')
      return
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      this.showError('File size must be less than 50MB.')
      return
    }

    // Store file for upload
    this.fileToUpload = file

    // Create image to validate dimensions
    const img = new Image()
    img.onload = () => {
      // Validate dimensions
      if (img.width !== img.height) {
        this.showError('Image must be square (same width and height).')
        return
      }
      
      if (img.width < 3000 || img.height < 3000) {
        this.showError(`Image must be at least 3000x3000 pixels. Current size: ${img.width}x${img.height}`)
        return
      }
      
      if (img.width > 6000 || img.height > 6000) {
        this.showError(`Image must be maximum 6000x6000 pixels. Current size: ${img.width}x${img.height}`)
        return
      }

      // All validations passed, upload immediately via AJAX
      this.uploadFileViaAjax(file, img.src)
    }

    img.onerror = () => {
      this.showError('Invalid image file.')
    }

    img.src = URL.createObjectURL(file)
  }

  async uploadFileViaAjax(file, previewSrc) {
    // Show progress
    this.showProgress()
    
    try {
      const formData = new FormData()
      formData.append('cover_image', file)
      
      // Get CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      
      const uploadUrl = `/albums/${this.albumIdValue}/upload_cover`
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Hide progress
        this.hideProgress()
        
        // Update the existing cover art preview instead of showing in drop zone
        this.updateExistingCoverArt(data.thumbnail_url)
        
        // Clear the input and reset drop zone to original state
        this.inputTarget.value = ''
        this.resetDropZone()
      } else {
        this.hideProgress()
        this.showError(data.errors.join(', '))
      }
    } catch (error) {
      this.hideProgress()
      this.showError('Upload failed. Please try again.')
      console.error('Upload error:', error)
    }
  }

  updateExistingCoverArt(thumbnailUrl) {
    if (this.hasCurrentImageTarget) {
      // Update the existing cover art image
      const img = this.currentImageTarget.querySelector('img')
      if (img) {
        img.src = thumbnailUrl
      }
      // Show the current image section
      this.currentImageTarget.style.display = 'block'
      
      // Hide the placeholder/gradient if it exists
      const placeholder = this.element.querySelector('.bg-gradient-to-br')
      if (placeholder && placeholder.parentElement) {
        placeholder.parentElement.style.display = 'none'
      }
    } else {
      // If no existing cover art section, create one
      this.createCoverArtSection(thumbnailUrl)
    }
  }

  createCoverArtSection(thumbnailUrl) {
    const coverArtHtml = `
      <div class="mb-4">
        <img src="${thumbnailUrl}" alt="Current cover" class="w-48 h-48 object-cover rounded-lg shadow-md">
        <p class="text-sm text-gray-500 mt-2">Current cover art</p>
      </div>
    `
    this.element.insertAdjacentHTML('afterbegin', coverArtHtml)
  }

  resetDropZone() {
    this.imagePreviewTarget.classList.add('hidden')
    this.uploadPromptTarget.classList.remove('hidden')
    this.hideError()
  }

  showUploadedImage(thumbnailUrl) {
    this.previewImageTarget.src = thumbnailUrl
    this.uploadPromptTarget.classList.add('hidden')
    this.imagePreviewTarget.classList.remove('hidden')
  }

  showPreview(imageSrc) {
    this.previewImageTarget.src = imageSrc
    this.uploadPromptTarget.classList.add('hidden')
    this.imagePreviewTarget.classList.remove('hidden')
  }

  removeImage() {
    this.inputTarget.value = ''
    this.imagePreviewTarget.classList.add('hidden')
    this.uploadPromptTarget.classList.remove('hidden')
    this.hideError()
  }

  showError(message) {
    this.uploadErrorTarget.querySelector('p').textContent = message
    this.uploadErrorTarget.classList.remove('hidden')
  }

  hideError() {
    this.uploadErrorTarget.classList.add('hidden')
  }

  showProgress() {
    this.uploadPromptTarget.classList.add('hidden')
    this.imagePreviewTarget.classList.add('hidden')
    this.uploadProgressTarget.classList.remove('hidden')
  }

  hideProgress() {
    this.uploadProgressTarget.classList.add('hidden')
  }

  updateProgress(percent) {
    this.progressBarTarget.style.width = `${percent}%`
  }
}